import { Injectable } from '@angular/core';
import { handleApiCallAsync } from '../../../../utils/general-functions';
import { IProjectContext, IProjectInDTO } from '../../../../models/project/project-model';
import { ProjectAuthApiService } from '../../../api/auth/projects/project-auth-api.service';
import { Subject } from 'rxjs';
import { WorkspaceProjectEventListenerService } from '../../workspace/socket/listeners/workspace-project-event-listener.service';

@Injectable()
export class ProjectMetadataManagerService {
    private _updatedProject$ = new Subject<IProjectInDTO>();
    public updatedProject$ = this._updatedProject$.asObservable();

    private _projectCreatedSubject = new Subject<IProjectInDTO>();
    public projectCreated$ = this._projectCreatedSubject.asObservable();

    private _projectDeletedSubject = new Subject<IProjectContext>();
    public projectDeleted$ = this._projectDeletedSubject.asObservable();
    constructor(
        private projectEventListener: WorkspaceProjectEventListenerService,
        private projectAuthApiService: ProjectAuthApiService
    ) {
        this.projectEventListener.onProjectCreated$.subscribe(async (data) => {
            if (!data?.projectId) {
                return;
            }
            const project = await this.getSpecificProjectMetadataAsync(data.projectId);
            if (!project) {
                return;
            }

            this._projectCreatedSubject.next(project);
        });
        this.projectEventListener.projectUpdatedToFetch$.subscribe(async (data) => {
            /// Only making this api call if I have any subscriptions to get the updated project
            /// So I won't make useless api calls for free
            if (!this._updatedProject$.observed) {
                console.log(`GOT A PROJECT TO FETCH BUT NO OBSERVED!`, data);
                return;
            }
            if (!data?.projectId) {
                return;
            }
            console.log(`GOT A PROJECT TO FETCH!`, data);
            const project = await this.getSpecificProjectMetadataAsync(data.projectId);
            if (!project) {
                return;
            }

            this._updatedProject$.next(project);
        });

        this.projectEventListener.onProjectDeleted$.subscribe((data) => {
            if (!data?.projectId) {
                return;
            }

            this._projectDeletedSubject.next({ projectId: data.projectId });
        });
    }

    updateProjectsList<T extends IProjectInDTO>(currentProjects: T[], newProject: T, maxItems: number | null): void {
        const projectIndex = currentProjects.findIndex((project) => project.id === newProject.id);

        // If the project exists, update it directly in place
        if (projectIndex !== -1) {
            Object.assign(currentProjects[projectIndex], newProject); // Mutate in place
            return;
        }

        // If max items is not reached, add the new project
        if (!maxItems || currentProjects.length < maxItems) {
            currentProjects.push(newProject);
            return;
        }

        // Find the oldest project and replace it
        if (currentProjects.length > 0) {
            const oldestProjectIndex = currentProjects.reduce((oldestIndex, project, index, array) => {
                return new Date(project.updatedAt).getTime() < new Date(array[oldestIndex].updatedAt).getTime()
                    ? index
                    : oldestIndex;
            }, 0); // Start with index 0 (ensuring a valid index)

            if (oldestProjectIndex !== -1) {
                Object.assign(currentProjects[oldestProjectIndex], newProject); // Mutate in place
            }
        }
    }

    async getSpecificProjectMetadataAsync(projectId: string) {
        const { data: projects, error } = await handleApiCallAsync(
            this.projectAuthApiService.getProjectById$(projectId)
        );
        if (error) {
            return null;
        }
        let newProject: IProjectInDTO;
        if (Array.isArray(projects)) {
            newProject = projects.find((project) => project.id === projectId);
        } else {
            newProject = projects;
        }

        return newProject;
    }
}
